import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, addDays, startOfWeek, parseISO, differenceInDays, isWithinInterval } from 'date-fns';
import { az, enUS, ru } from 'date-fns/locale';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  ChevronLeft, ChevronRight, Plus, X, Calendar as CalendarIcon,
  Phone, User, DollarSign, FileText
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Reservations = () => {
  const { t, language } = useLanguage();
  const { token } = useAuth();
  
  const [cottages, setCottages] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [daysToShow, setDaysToShow] = useState(7);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [formData, setFormData] = useState({
    cottage_id: '',
    customer_name: '',
    phone: '',
    check_in: '',
    check_out: '',
    total_amount: 0,
    deposit: 0,
    payment_status: 'unpaid',
    payment_method: '',
    notes: ''
  });

  const locale = language === 'az' ? az : language === 'ru' ? ru : enUS;

  const fetchData = useCallback(async () => {
    try {
      const endDate = addDays(startDate, daysToShow);
      const [cottagesRes, reservationsRes] = await Promise.all([
        axios.get(`${API_URL}/api/cottages`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/reservations`, {
          params: {
            start_date: format(startDate, 'yyyy-MM-dd'),
            end_date: format(endDate, 'yyyy-MM-dd')
          },
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setCottages(cottagesRes.data);
      setReservations(reservationsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token, startDate, daysToShow]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const days = Array.from({ length: daysToShow }, (_, i) => addDays(startDate, i));

  const getReservationForCell = (cottageId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return reservations.find(r => {
      if (r.cottage_id !== cottageId) return false;
      const checkIn = parseISO(r.check_in);
      const checkOut = parseISO(r.check_out);
      return isWithinInterval(date, { start: checkIn, end: checkOut }) ||
             dateStr === r.check_in || dateStr === r.check_out;
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'booked-paid';
      case 'deposit': return 'booked-deposit';
      case 'unpaid': return 'booked-unpaid';
      default: return 'empty';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid': return { bg: 'badge-paid', text: t('reservation_paid') };
      case 'deposit': return { bg: 'badge-deposit', text: t('reservation_deposit_paid') };
      case 'unpaid': return { bg: 'badge-unpaid', text: t('reservation_unpaid') };
      default: return { bg: 'bg-gray-100', text: t('reservation_empty') };
    }
  };

  const handlePrevious = () => {
    setStartDate(addDays(startDate, -daysToShow));
  };

  const handleNext = () => {
    setStartDate(addDays(startDate, daysToShow));
  };

  const handleToday = () => {
    setStartDate(startOfWeek(new Date(), { weekStartsOn: 1 }));
  };

  const openNewReservation = (cottageId = '', date = null) => {
    setSelectedReservation(null);
    setFormData({
      cottage_id: cottageId,
      customer_name: '',
      phone: '',
      check_in: date ? format(date, 'yyyy-MM-dd') : '',
      check_out: date ? format(addDays(date, 1), 'yyyy-MM-dd') : '',
      total_amount: 0,
      deposit: 0,
      payment_status: 'unpaid',
      payment_method: '',
      notes: ''
    });
    setIsModalOpen(true);
  };

  const openEditReservation = (reservation) => {
    setSelectedReservation(reservation);
    setFormData({
      cottage_id: reservation.cottage_id,
      customer_name: reservation.customer_name,
      phone: reservation.phone,
      check_in: reservation.check_in,
      check_out: reservation.check_out,
      total_amount: reservation.total_amount,
      deposit: reservation.deposit,
      payment_status: reservation.payment_status,
      payment_method: reservation.payment_method || '',
      notes: reservation.notes || ''
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedReservation) {
        await axios.put(`${API_URL}/api/reservations/${selectedReservation.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Rezervasiya yeniləndi');
      } else {
        await axios.post(`${API_URL}/api/reservations`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Rezervasiya əlavə edildi');
      }
      setIsModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving reservation:', error);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409) {
        toast.error(typeof detail === 'string' ? detail : 'Bu kotec seçilmiş tarixlərdə artıq rezerv olunub');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Rezervasiya yadda saxlanılmadı');
      }
    }
  };

  const handleDelete = async () => {
    if (!selectedReservation || !window.confirm('Bu rezervasiyanı silmək istədiyinizə əminsiniz?')) return;
    try {
      await axios.delete(`${API_URL}/api/reservations/${selectedReservation.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error deleting reservation:', error);
    }
  };

  const formatCurrency = (amount) => `AZN ${(amount || 0).toFixed(2)}`;

  // Calculate stats
  const totalDeposit = reservations.reduce((sum, r) => sum + (r.deposit || 0), 0);
  const totalAmount = reservations.reduce((sum, r) => sum + (r.total_amount || 0), 0);
  const remaining = totalAmount - totalDeposit;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2D5016]"></div>
      </div>
    );
  }

  return (
    <div data-testid="reservations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A2415] font-['Cormorant_Garamond']">
            {t('reservation_calendar')}
          </h1>
          <p className="text-[#4A5D43] text-sm mt-1">Bütün kotteclərin rezervasiya vəziyyəti</p>
        </div>
        <Button 
          onClick={() => openNewReservation()}
          className="bg-[#2D5016] hover:bg-[#1A2415]"
          data-testid="new-reservation-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('reservation_new')}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="stat-card border-l-4 border-l-[#2D5016]">
          <p className="text-[#4A5D43] text-sm">{t('stats_total_deposit')}</p>
          <p className="text-2xl font-semibold text-[#2D5016]">{formatCurrency(totalDeposit)}</p>
        </div>
        <div className="stat-card border-l-4 border-l-[#D97706]">
          <p className="text-[#4A5D43] text-sm">{t('stats_total_reservations')}</p>
          <p className="text-2xl font-semibold text-[#D97706]">{formatCurrency(totalAmount)}</p>
        </div>
        <div className="stat-card border-l-4 border-l-red-500">
          <p className="text-[#4A5D43] text-sm">{t('stats_remaining')}</p>
          <p className="text-2xl font-semibold text-red-500">{formatCurrency(remaining)}</p>
        </div>
      </div>

      {/* Legend & Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded calendar-cell empty"></div>
            <span className="text-sm text-[#4A5D43]">{t('reservation_empty')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded badge-paid"></div>
            <span className="text-sm text-[#4A5D43]">{t('reservation_paid')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded badge-deposit"></div>
            <span className="text-sm text-[#4A5D43]">{t('reservation_deposit_paid')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded badge-unpaid"></div>
            <span className="text-sm text-[#4A5D43]">{t('reservation_unpaid')}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Select value={daysToShow.toString()} onValueChange={(v) => setDaysToShow(parseInt(v))}>
            <SelectTrigger className="w-28" data-testid="days-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 Gün</SelectItem>
              <SelectItem value="14">{t('days_14')}</SelectItem>
              <SelectItem value="30">30 Gün</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleToday} data-testid="today-btn">
            {t('today')}
          </Button>
        </div>
      </div>

      {/* Calendar Navigation */}
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" onClick={handlePrevious} data-testid="prev-btn">
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <h2 className="text-lg font-medium text-[#1A2415]">
          {format(startDate, 'dd MMM', { locale })} - {format(addDays(startDate, daysToShow - 1), 'dd MMM yyyy', { locale })}
        </h2>
        <Button variant="ghost" onClick={handleNext} data-testid="next-btn">
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white border border-[#E5E3DB] rounded-lg overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead>
            <tr className="border-b border-[#E5E3DB]">
              <th className="sticky left-0 bg-white z-10 p-3 text-left text-sm font-medium text-[#1A2415] w-40 border-r border-[#E5E3DB]">
                Kottec
              </th>
              {days.map((day, i) => {
                const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                const dayName = format(day, 'EEE', { locale });
                const dayNum = format(day, 'dd.MM');
                return (
                  <th 
                    key={i} 
                    className={`p-2 text-center min-w-[80px] ${isToday ? 'bg-[#2D5016] text-white' : 'text-[#4A5D43]'}`}
                  >
                    <div className="text-xs font-medium">{dayName}</div>
                    <div className="text-sm font-semibold">{dayNum}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {cottages.map((cottage) => (
              <tr key={cottage.id} className="border-b border-[#E5E3DB] last:border-b-0">
                <td className="sticky left-0 bg-white z-10 p-3 border-r border-[#E5E3DB]">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[#2D5016]"></div>
                    <div>
                      <p className="font-medium text-[#1A2415] text-sm">{cottage.name}</p>
                      <p className="text-xs text-[#4A5D43]">{cottage.capacity} {t('cottages_capacity')}</p>
                    </div>
                  </div>
                </td>
                {days.map((day, i) => {
                  const reservation = getReservationForCell(cottage.id, day);
                  const isFirstDay = reservation && reservation.check_in === format(day, 'yyyy-MM-dd');
                  
                  return (
                    <td 
                      key={i} 
                      className={`calendar-cell ${reservation ? getStatusColor(reservation.payment_status) : 'empty'} cursor-pointer hover:opacity-80 transition-opacity`}
                      onClick={() => reservation ? openEditReservation(reservation) : openNewReservation(cottage.id, day)}
                      data-testid={`cell-${cottage.id}-${format(day, 'yyyy-MM-dd')}`}
                    >
                      {reservation && isFirstDay && (
                        <div className="p-2 text-xs">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs mb-1 ${getStatusBadge(reservation.payment_status).bg}`}>
                            {getStatusBadge(reservation.payment_status).text}
                          </span>
                          <p className="font-medium text-[#1A2415] truncate">{reservation.customer_name}</p>
                          <p className="text-[#4A5D43]">{reservation.phone}</p>
                          <p className="text-[#D97706] font-medium">{formatCurrency(reservation.total_amount)}</p>
                        </div>
                      )}
                      {!reservation && (
                        <div className="p-2 text-center text-xs text-[#4A5D43]">
                          {t('reservation_empty')}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Reservation Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{selectedReservation ? 'Rezervasiyanı Redaktə Et' : t('reservation_new')}</span>
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Cottage Selection */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <CalendarIcon className="w-4 h-4" />
                Kottec Seçimi
              </Label>
              <div className="grid grid-cols-2 gap-2 p-3 bg-[#F9F8F6] rounded-lg max-h-32 overflow-y-auto">
                {cottages.map((cottage) => (
                  <label 
                    key={cottage.id}
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                      formData.cottage_id === cottage.id ? 'bg-[#2D5016]/10 border border-[#2D5016]' : 'hover:bg-white'
                    }`}
                  >
                    <input
                      type="radio"
                      name="cottage_id"
                      value={cottage.id}
                      checked={formData.cottage_id === cottage.id}
                      onChange={(e) => setFormData({ ...formData, cottage_id: e.target.value })}
                      className="accent-[#2D5016]"
                      required
                    />
                    <div>
                      <p className="text-sm font-medium text-[#1A2415]">{cottage.name}</p>
                      <p className="text-xs text-[#4A5D43]">{cottage.capacity} kişilik</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Customer Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <User className="w-4 h-4" />
                  {t('reservation_customer')}
                </Label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  required
                  data-testid="customer-name-input"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Phone className="w-4 h-4" />
                  {t('reservation_phone')}
                </Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  data-testid="phone-input"
                />
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-2 block">{t('reservation_checkin')}</Label>
                <Input
                  type="date"
                  value={formData.check_in}
                  onChange={(e) => setFormData({ ...formData, check_in: e.target.value })}
                  required
                  data-testid="checkin-input"
                />
              </div>
              <div>
                <Label className="mb-2 block">{t('reservation_checkout')}</Label>
                <Input
                  type="date"
                  value={formData.check_out}
                  onChange={(e) => setFormData({ ...formData, check_out: e.target.value })}
                  required
                  data-testid="checkout-input"
                />
              </div>
            </div>

            {/* Amount */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-4 h-4" />
                  {t('reservation_total')} (AZN)
                </Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.total_amount}
                  onChange={(e) => {
                    const total = parseFloat(e.target.value) || 0;
                    setFormData((prev) => {
                      let deposit = prev.deposit;
                      if (prev.payment_status === 'paid') deposit = total;
                      else if (prev.payment_status === 'unpaid') deposit = 0;
                      else if (deposit > total) deposit = total;
                      return { ...prev, total_amount: total, deposit };
                    });
                  }}
                  data-testid="total-input"
                />
              </div>
              <div>
                <Label className="mb-2 block">{t('reservation_deposit')} (AZN)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.deposit}
                  disabled={formData.payment_status === 'paid' || formData.payment_status === 'unpaid'}
                  onChange={(e) => {
                    const dep = parseFloat(e.target.value) || 0;
                    setFormData((prev) => ({ ...prev, deposit: Math.min(dep, prev.total_amount || 0) }));
                  }}
                  data-testid="deposit-input"
                />
              </div>
            </div>

            {/* Remaining (live) */}
            <div className="bg-[#F9F8F6] border border-[#E5E3DB] rounded-lg p-3 flex justify-between items-center">
              <span className="text-sm text-[#4A5D43]">{t('customer_remaining') || 'Qalıq'}:</span>
              <span
                className={`text-lg font-semibold ${
                  (formData.total_amount - formData.deposit) <= 0 ? 'text-green-600' : 'text-red-500'
                }`}
                data-testid="remaining-amount"
              >
                AZN {Math.max(0, (parseFloat(formData.total_amount) || 0) - (parseFloat(formData.deposit) || 0)).toFixed(2)}
              </span>
            </div>

            {/* Status & Method */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-2 block">{t('reservation_status')}</Label>
                <Select 
                  value={formData.payment_status} 
                  onValueChange={(v) => setFormData((prev) => {
                    let deposit = prev.deposit;
                    const total = parseFloat(prev.total_amount) || 0;
                    if (v === 'paid') deposit = total;
                    else if (v === 'unpaid') deposit = 0;
                    return { ...prev, payment_status: v, deposit };
                  })}
                >
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unpaid">{t('reservation_unpaid')}</SelectItem>
                    <SelectItem value="deposit">{t('reservation_deposit_paid')}</SelectItem>
                    <SelectItem value="paid">{t('reservation_paid')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-2 block">{t('reservation_method')}</Label>
                <Select 
                  value={formData.payment_method || ''} 
                  onValueChange={(v) => setFormData({ ...formData, payment_method: v })}
                >
                  <SelectTrigger data-testid="method-select">
                    <SelectValue placeholder="Seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Nağd</SelectItem>
                    <SelectItem value="card">Kart</SelectItem>
                    <SelectItem value="transfer">Köçürmə</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4" />
                {t('reservation_notes')}
              </Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                data-testid="notes-input"
              />
            </div>

            {/* Remaining calculation */}
            {formData.total_amount > 0 && (
              <div className="p-3 bg-[#F9F8F6] rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-[#4A5D43]">Beh:</span>
                  <span className="text-[#2D5016] font-medium">{formatCurrency(formData.deposit)}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-[#4A5D43]">Qalıq:</span>
                  <span className="text-red-500 font-medium">{formatCurrency(formData.total_amount - formData.deposit)}</span>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-between pt-4 border-t">
              {selectedReservation && (
                <Button 
                  type="button" 
                  variant="destructive" 
                  onClick={handleDelete}
                  data-testid="delete-reservation-btn"
                >
                  {t('delete')}
                </Button>
              )}
              <div className="flex gap-2 ml-auto">
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                  {t('cancel')}
                </Button>
                <Button type="submit" className="bg-[#2D5016] hover:bg-[#1A2415]" data-testid="save-reservation-btn">
                  {t('save')}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Reservations;
